import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Meal, MealSlot } from '@pacer/shared';
import { MEAL_SLOTS } from '@pacer/shared';
import * as api from '../api';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Button, Card, Field, IconButton, Input, PageHeader, Spinner, Textarea, cx } from '../components/ui';
import { useZone } from '../lib/dates';
import { queryKeys } from '../state/queries';
import { confirmDelete, toast } from '../state/ui';

const SLOT_LABEL: Record<MealSlot, string> = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner' };

export function Meals() {
  const zone = useZone();
  // Meal planning is forward-looking, so anchor the week to TODAY (a rolling 7 days) rather
  // than Monday — otherwise mid-week you stare at days that have already been and gone.
  const [weekStart, setWeekStart] = useState(() => DateTime.now().setZone(zone).startOf('day'));
  const [editing, setEditing] = useState<{ date: string; slot: MealSlot; meal?: Meal } | null>(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i })), [weekStart]);
  const from = days[0]!.toISODate()!;
  const to = days[6]!.toISODate()!;

  const { data: meals = [], isLoading } = useQuery({
    queryKey: [...queryKeys.meals, from, to],
    queryFn: () => api.getMeals(from, to),
  });

  const byKey = useMemo(() => {
    const m = new Map<string, Meal>();
    for (const meal of meals) m.set(`${meal.date}|${meal.slot}`, meal);
    return m;
  }, [meals]);

  const todayIso = DateTime.now().setZone(zone).toISODate();

  if (isLoading) return <Spinner label="loading" />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:px-8 md:py-10">
      <PageHeader
        eyebrow={`${weekStart.toFormat('LLL d')} – ${days[6]!.toFormat('LLL d')}`}
        title="Meals"
        actions={
          <div className="flex items-center gap-1.5">
            <IconButton icon="chevron-left" label="Previous week" onClick={() => setWeekStart((w) => w.minus({ weeks: 1 }))} />
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(DateTime.now().setZone(zone).startOf('day'))}>
              Today
            </Button>
            <IconButton icon="chevron-right" label="Next week" onClick={() => setWeekStart((w) => w.plus({ weeks: 1 }))} />
          </div>
        }
      />

      <div className="flex flex-col gap-3">
        {days.map((day) => {
          const iso = day.toISODate()!;
          const isToday = iso === todayIso;
          return (
            <Card key={iso} className={cx('flex gap-3 p-3', isToday && 'border-accent/40')}>
              <div className="w-14 shrink-0 pt-1 text-center">
                <div className="eyebrow">{day.toFormat('ccc')}</div>
                <div className={cx('tabular font-mono text-[22px]', isToday ? 'text-accent' : 'text-primary')}>
                  {day.day}
                </div>
              </div>

              <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-3">
                {MEAL_SLOTS.map((slot) => {
                  const meal = byKey.get(`${iso}|${slot}`);
                  return (
                    <button
                      key={slot}
                      onClick={() => setEditing({ date: iso, slot, meal })}
                      className={cx(
                        'flex min-h-[52px] flex-col rounded-md border px-2.5 py-2 text-left transition-colors',
                        meal
                          ? 'border-border-subtle bg-surface-high/50 hover:border-border'
                          : 'border-dashed border-border-subtle hover:border-border hover:bg-surface-high/30',
                      )}
                    >
                      <span className="eyebrow">{SLOT_LABEL[slot]}</span>
                      {meal ? (
                        <span className="mt-0.5 flex items-center gap-1.5 text-[14px] text-primary">
                          <span className="truncate">{meal.title}</span>
                          {meal.ingredients && <Icon name="cart" size={12} className="shrink-0 text-muted" />}
                        </span>
                      ) : (
                        <span className="mt-0.5 text-[13px] text-text-sub">+ add</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {editing && (
        <MealEditor
          date={editing.date}
          slot={editing.slot}
          meal={editing.meal}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function MealEditor({
  date,
  slot,
  meal,
  onClose,
}: {
  date: string;
  slot: MealSlot;
  meal?: Meal;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(meal?.title ?? '');
  const [ingredients, setIngredients] = useState(meal?.ingredients ?? '');
  const [notes, setNotes] = useState(meal?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  const { data: lists = [] } = useQuery({ queryKey: queryKeys.lists, queryFn: api.getLists });
  const shoppingList = lists.find((l) => l.type === 'shopping');

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.meals });
    onClose();
  };

  const save = useMutation({
    mutationFn: () =>
      api.saveMeal({ date, slot, title: title.trim(), notes: notes.trim() || null, ingredients: ingredients.trim() || null }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMeal(meal!.id),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  // Save first (so the ingredients persist), then push them to the shopping list.
  const toGroceries = useMutation({
    mutationFn: async (): Promise<number> => {
      const saved = await api.saveMeal({
        date,
        slot,
        title: title.trim(),
        notes: notes.trim() || null,
        ingredients: ingredients.trim() || null,
      });
      const r = await api.mealToGroceries(saved.id, shoppingList!.id);
      return r.added;
    },
    onSuccess: async (added) => {
      setSent(added);
      await qc.invalidateQueries({ queryKey: queryKeys.meals });
      await qc.invalidateQueries({ queryKey: queryKeys.lists });
      toast(`Added ${added} ${added === 1 ? 'item' : 'items'} to ${shoppingList!.name}`, 'success');
    },
    onError: (e: Error) => setError(e.message),
  });

  const ingredientCount = ingredients.split('\n').map((l) => l.trim()).filter(Boolean).length;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={`${SLOT_LABEL[slot]} · ${DateTime.fromISO(date).toFormat('ccc, LLL d')}`}
      title={meal ? 'Edit meal' : 'Plan a meal'}
      footer={
        <>
          {meal && (
            <Button
              variant="danger"
              onClick={async () => {
                if (await confirmDelete('this meal')) remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Remove
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!title.trim() || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Meal">
          <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tacos" autoCapitalize="sentences" />
        </Field>

        <Field label="Ingredients" hint="One per line — these become shopping list items.">
          <Textarea
            rows={5}
            value={ingredients}
            onChange={(e) => {
              setIngredients(e.target.value);
              setSent(null);
            }}
            placeholder={'Ground beef\nTortillas\nCheese\nSalsa'}
          />
        </Field>

        {shoppingList && ingredientCount > 0 && (
          <Button
            variant="secondary"
            icon="cart"
            onClick={() => toGroceries.mutate()}
            disabled={toGroceries.isPending}
          >
            {toGroceries.isPending
              ? 'Adding…'
              : sent !== null
                ? `Added ${sent} to ${shoppingList.name}`
                : `Add ${ingredientCount} to ${shoppingList.name}`}
          </Button>
        )}

        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
